import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ArrowRight, Clock, Info, Plus, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

interface StepTimeSlotsProps {
  onComplete: (data: { slots: Array<{ startTime: string; endTime: string; days: number[] }> }) => void;
  onBack: () => void;
  onSkip: () => void;
}

type PresetType = 'midi' | 'soir' | 'both';

interface DayGroup {
  id: string;
  days: number[];
  hasMidi: boolean;
  hasSoir: boolean;
  midiStart: string;
  midiEnd: string;
  soirStart: string;
  soirEnd: string;
}

const WEEKDAYS = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mer' },
  { value: 4, label: 'Jeu' },
  { value: 5, label: 'Ven' },
  { value: 6, label: 'Sam' },
  { value: 0, label: 'Dim' },
];

const DEFAULT_PRESETS = {
  midi: { startTime: '12:00', endTime: '14:30' },
  soir: { startTime: '19:00', endTime: '22:00' },
};

const createDefaultGroup = (id: string, days: number[] = []): DayGroup => ({
  id,
  days,
  hasMidi: true,
  hasSoir: true,
  midiStart: DEFAULT_PRESETS.midi.startTime,
  midiEnd: DEFAULT_PRESETS.midi.endTime,
  soirStart: DEFAULT_PRESETS.soir.startTime,
  soirEnd: DEFAULT_PRESETS.soir.endTime,
});

export function StepTimeSlots({ onComplete, onBack, onSkip }: StepTimeSlotsProps) {
  // Mode selection: simple or advanced
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  
  // Simple mode state
  const [preset, setPreset] = useState<PresetType>('both');
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5, 6]);
  const [midiStart, setMidiStart] = useState(DEFAULT_PRESETS.midi.startTime);
  const [midiEnd, setMidiEnd] = useState(DEFAULT_PRESETS.midi.endTime);
  const [soirStart, setSoirStart] = useState(DEFAULT_PRESETS.soir.startTime);
  const [soirEnd, setSoirEnd] = useState(DEFAULT_PRESETS.soir.endTime);

  // Advanced mode state
  const [dayGroups, setDayGroups] = useState<DayGroup[]>([
    createDefaultGroup('group-1', [1, 2, 3, 4, 5]),
  ]);

  const toggleDay = (day: number) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleModeChange = (advanced: boolean) => {
    if (advanced && !isAdvancedMode) {
      // Transition from simple to advanced: pre-fill with current data
      const initialGroup = createDefaultGroup('group-1', selectedDays);
      initialGroup.hasMidi = preset === 'midi' || preset === 'both';
      initialGroup.hasSoir = preset === 'soir' || preset === 'both';
      initialGroup.midiStart = midiStart;
      initialGroup.midiEnd = midiEnd;
      initialGroup.soirStart = soirStart;
      initialGroup.soirEnd = soirEnd;
      setDayGroups([initialGroup]);
    }
    setIsAdvancedMode(advanced);
  };

  // Get all days used across all groups
  const getUsedDays = (): number[] => {
    return dayGroups.flatMap(g => g.days);
  };

  // Get available days for a specific group (excluding days used by other groups)
  const getAvailableDaysForGroup = (groupId: string): number[] => {
    const usedByOthers = dayGroups
      .filter(g => g.id !== groupId)
      .flatMap(g => g.days);
    return WEEKDAYS.map(d => d.value).filter(d => !usedByOthers.includes(d));
  };

  const toggleDayInGroup = (groupId: string, day: number) => {
    setDayGroups(prev => prev.map(group => {
      if (group.id !== groupId) return group;
      return {
        ...group,
        days: group.days.includes(day)
          ? group.days.filter(d => d !== day)
          : [...group.days, day],
      };
    }));
  };

  const updateGroup = (groupId: string, updates: Partial<DayGroup>) => {
    setDayGroups(prev => prev.map(group =>
      group.id === groupId ? { ...group, ...updates } : group
    ));
  };

  const addGroup = () => {
    const usedDays = getUsedDays();
    const availableDays = WEEKDAYS.map(d => d.value).filter(d => !usedDays.includes(d));
    const newGroup = createDefaultGroup(`group-${Date.now()}`, availableDays.length > 0 ? [availableDays[0]] : []);
    setDayGroups(prev => [...prev, newGroup]);
  };

  const removeGroup = (groupId: string) => {
    setDayGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const handleSubmit = () => {
    const slots: Array<{ startTime: string; endTime: string; days: number[] }> = [];
    
    if (isAdvancedMode) {
      // Advanced mode: generate slots from day groups
      for (const group of dayGroups) {
        if (group.days.length === 0) continue;
        
        if (group.hasMidi) {
          slots.push({
            startTime: group.midiStart,
            endTime: group.midiEnd,
            days: group.days,
          });
        }
        if (group.hasSoir) {
          slots.push({
            startTime: group.soirStart,
            endTime: group.soirEnd,
            days: group.days,
          });
        }
      }
    } else {
      // Simple mode: same as before
      if (preset === 'midi' || preset === 'both') {
        slots.push({
          startTime: midiStart,
          endTime: midiEnd,
          days: selectedDays,
        });
      }
      if (preset === 'soir' || preset === 'both') {
        slots.push({
          startTime: soirStart,
          endTime: soirEnd,
          days: selectedDays,
        });
      }
    }
    
    onComplete({ slots });
  };

  // Check if we can add more groups (not all days are used)
  const canAddGroup = getUsedDays().length < 7;

  // Validation for advanced mode
  const isAdvancedValid = dayGroups.every(g => 
    g.days.length > 0 && (g.hasMidi || g.hasSoir)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Clock className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Vos services</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Définissez les créneaux de réservation
        </p>
      </div>

      {/* Explanation */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <p>
            Un <strong>service</strong> est une plage horaire pendant laquelle vous acceptez des réservations.
            Par exemple : service du midi (12h-14h30), service du soir (19h-22h).
          </p>
        </AlertDescription>
      </Alert>

      {/* Mode selection */}
      <div className="space-y-3">
        <Label>Vos horaires sont-ils identiques tous les jours ?</Label>
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant={!isAdvancedMode ? 'default' : 'outline'}
            onClick={() => handleModeChange(false)}
            className="h-auto py-3 px-4"
          >
            <div className="text-left">
              <p className="font-medium">Oui, mêmes horaires</p>
              <p className="text-xs opacity-70 font-normal">Configuration rapide</p>
            </div>
          </Button>
          <Button
            type="button"
            variant={isAdvancedMode ? 'default' : 'outline'}
            onClick={() => handleModeChange(true)}
            className="h-auto py-3 px-4"
          >
            <div className="text-left">
              <p className="font-medium">Non, ça varie</p>
              <p className="text-xs opacity-70 font-normal">Ex: différent le weekend</p>
            </div>
          </Button>
        </div>
      </div>

      {/* Simple Mode */}
      {!isAdvancedMode && (
        <div className="space-y-4">
          {/* Days selection */}
          <div className="space-y-3">
            <Label>Jours d'ouverture</Label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map(day => (
                <Button
                  key={day.value}
                  type="button"
                  variant={selectedDays.includes(day.value) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleDay(day.value)}
                  className={cn(
                    "min-w-[3.5rem]",
                    selectedDays.includes(day.value) && "bg-primary text-primary-foreground"
                  )}
                >
                  {day.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Presets */}
          <div className="space-y-3">
            <Label>Services proposés</Label>
            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                variant={preset === 'midi' ? 'default' : 'outline'}
                onClick={() => setPreset('midi')}
                className="w-full"
              >
                Midi
              </Button>
              <Button
                type="button"
                variant={preset === 'soir' ? 'default' : 'outline'}
                onClick={() => setPreset('soir')}
                className="w-full"
              >
                Soir
              </Button>
              <Button
                type="button"
                variant={preset === 'both' ? 'default' : 'outline'}
                onClick={() => setPreset('both')}
                className="w-full"
              >
                Les deux
              </Button>
            </div>
          </div>

          {/* Time slots configuration */}
          <div className="space-y-4">
            {/* Midi */}
            {(preset === 'midi' || preset === 'both') && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <Label className="font-medium">Service du midi</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="time"
                    value={midiStart}
                    onChange={(e) => setMidiStart(e.target.value)}
                    className="w-32"
                  />
                  <span className="text-muted-foreground">→</span>
                  <Input
                    type="time"
                    value={midiEnd}
                    onChange={(e) => setMidiEnd(e.target.value)}
                    className="w-32"
                  />
                </div>
              </div>
            )}

            {/* Soir */}
            {(preset === 'soir' || preset === 'both') && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <Label className="font-medium">Service du soir</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="time"
                    value={soirStart}
                    onChange={(e) => setSoirStart(e.target.value)}
                    className="w-32"
                  />
                  <span className="text-muted-foreground">→</span>
                  <Input
                    type="time"
                    value={soirEnd}
                    onChange={(e) => setSoirEnd(e.target.value)}
                    className="w-32"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Advanced Mode */}
      {isAdvancedMode && (
        <div className="space-y-4">
          {dayGroups.map((group, index) => (
            <DayGroupCard
              key={group.id}
              group={group}
              groupIndex={index}
              availableDays={getAvailableDaysForGroup(group.id)}
              onToggleDay={(day) => toggleDayInGroup(group.id, day)}
              onUpdate={(updates) => updateGroup(group.id, updates)}
              onRemove={() => removeGroup(group.id)}
              canRemove={dayGroups.length > 1}
            />
          ))}

          {canAddGroup && (
            <Button
              type="button"
              variant="outline"
              onClick={addGroup}
              className="w-full border-dashed"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un groupe de jours
            </Button>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={onBack} className="flex-1">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Button variant="ghost" onClick={onSkip}>
          Passer
        </Button>
        <Button 
          onClick={handleSubmit} 
          className="flex-1"
          disabled={isAdvancedMode && !isAdvancedValid}
        >
          Continuer
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

// Sub-component for day group cards in advanced mode
interface DayGroupCardProps {
  group: DayGroup;
  groupIndex: number;
  availableDays: number[];
  onToggleDay: (day: number) => void;
  onUpdate: (updates: Partial<DayGroup>) => void;
  onRemove: () => void;
  canRemove: boolean;
}

function DayGroupCard({
  group,
  groupIndex,
  availableDays,
  onToggleDay,
  onUpdate,
  onRemove,
  canRemove,
}: DayGroupCardProps) {
  const selectableDays = [...group.days, ...availableDays];

  return (
    <div className="p-4 border rounded-lg space-y-4 bg-card">
      <div className="flex items-center justify-between">
        <Label className="font-medium">Groupe {groupIndex + 1}</Label>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Days selection for this group */}
      <div className="space-y-2">
        <Label className="text-sm text-muted-foreground">Jours</Label>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map(day => {
            const isSelected = group.days.includes(day.value);
            const isAvailable = selectableDays.includes(day.value);
            
            return (
              <Button
                key={day.value}
                type="button"
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                onClick={() => onToggleDay(day.value)}
                disabled={!isAvailable}
                className={cn(
                  "min-w-[3.5rem]",
                  isSelected && "bg-primary text-primary-foreground",
                  !isAvailable && "opacity-30"
                )}
              >
                {day.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Services for this group */}
      <div className="space-y-3">
        {/* Midi service */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${group.id}-midi`}
              checked={group.hasMidi}
              onCheckedChange={(checked) => onUpdate({ hasMidi: !!checked })}
            />
            <Label htmlFor={`${group.id}-midi`} className="text-sm cursor-pointer">
              Service du midi
            </Label>
          </div>
          {group.hasMidi && (
            <div className="flex items-center gap-3 ml-6">
              <Input
                type="time"
                value={group.midiStart}
                onChange={(e) => onUpdate({ midiStart: e.target.value })}
                className="w-28"
              />
              <span className="text-muted-foreground text-sm">→</span>
              <Input
                type="time"
                value={group.midiEnd}
                onChange={(e) => onUpdate({ midiEnd: e.target.value })}
                className="w-28"
              />
            </div>
          )}
        </div>

        {/* Soir service */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${group.id}-soir`}
              checked={group.hasSoir}
              onCheckedChange={(checked) => onUpdate({ hasSoir: !!checked })}
            />
            <Label htmlFor={`${group.id}-soir`} className="text-sm cursor-pointer">
              Service du soir
            </Label>
          </div>
          {group.hasSoir && (
            <div className="flex items-center gap-3 ml-6">
              <Input
                type="time"
                value={group.soirStart}
                onChange={(e) => onUpdate({ soirStart: e.target.value })}
                className="w-28"
              />
              <span className="text-muted-foreground text-sm">→</span>
              <Input
                type="time"
                value={group.soirEnd}
                onChange={(e) => onUpdate({ soirEnd: e.target.value })}
                className="w-28"
              />
            </div>
          )}
        </div>
      </div>

      {/* Validation message */}
      {group.days.length === 0 && (
        <p className="text-sm text-destructive">Sélectionnez au moins un jour</p>
      )}
      {group.days.length > 0 && !group.hasMidi && !group.hasSoir && (
        <p className="text-sm text-destructive">Activez au moins un service</p>
      )}
    </div>
  );
}
